// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The write layer for the gate. Two mutations, both fail-closed on the server:
//   * useApproveReportingGate — approve | return | reopen via approve_reporting_gate. The
//     server RE-DERIVES the full gate condition and REFUSES on any doubt: reconciliation.all_pass
//     + no unresolved blocking flag + no failing blocking ledger check + RENDER-BEFORE-GATE
//     (cycle reached render AND a frozen reporting_artifact_versions row bound to the current IR
//     hash exists for EVERY deck/newsletter kind the IR declares in ir.views, and ir.views must
//     map to at least one recognized kind). A disabled client button is UX only — the RPC is the
//     wall. Investment, Operations OR admin/superadmin may sign, per the CIO ruling of 2026-08-17
//     (migration 0003r); a caller with no auth.uid() is still refused outright, and four-eyes leg
//     (ii-b) is role-independent so the resolver of a blocking flag still cannot be the signer.
//     resolve_reporting_flag below was NOT widened and stays Investment/Ops. Signature unchanged
//     (_cycle_id/_decision/_note); the render/artifact strictness is entirely server-side.
//   * useResolveReportingFlag — clear a blocking, human-resolvable flag via
//     resolve_reporting_flag so the gate becomes satisfiable (e.g. the Nomura flag-002).
//
// On success both invalidate the cycle + list queries so the shell reflects the new state.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type GateDecision = "approve" | "return" | "reopen";

export function useApproveReportingGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      decision,
      note,
    }: {
      cycleId: string;
      decision: GateDecision;
      note?: string | null;
    }) => {
      const { data, error } = await (supabase as any).rpc("approve_reporting_gate", {
        _cycle_id: cycleId,
        _decision: decision,
        _note: note ?? null,
      });
      if (error) throw error;
      return data as Record<string, any>;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["get_reporting_cycle", vars.cycleId] });
      qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
    },
  });
}

export function useResolveReportingFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cycleId,
      flagId,
      note,
    }: {
      cycleId: string;
      flagId: string;
      note?: string | null;
    }) => {
      const { error } = await (supabase as any).rpc("resolve_reporting_flag", {
        _cycle_id: cycleId,
        _flag_id: flagId,
        _note: note ?? null,
      });
      if (error) throw error;
      return true;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["get_reporting_cycle", vars.cycleId] });
      qc.invalidateQueries({ queryKey: ["list_reporting_cycles"] });
    },
  });
}
